import {
  type BrowserSessionSnapshot,
  type DevTunnelRecord,
  type EnvironmentApi,
  type EnvironmentId,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";
import { type MutableRefObject, useCallback } from "react";
import { useBrowserStateStore } from "~/browserStateStore";
import { runThreadExecutionIntentAction } from "~/lib/runThreadExecutionIntentAction";
import { newCommandId } from "~/lib/utils";
import { toastManager } from "./ui/toast";
import {
  browserSessionHasVisibleAddressIntent,
  browserSessionOpenAttemptCount,
  browserSessionUnavailableMessage,
  shouldOpenBrowserSession,
  shouldRetryBrowserSessionOpenError,
  shouldRetryHiddenLocalPreviewSessionOpen,
  withBrowserOperationTimeout,
} from "./BrowserPreviewPanel.logic";

const BROWSER_SESSION_START_TIMEOUT_MS = 180_000;
const BROWSER_SESSION_CLOSE_TIMEOUT_MS = 12_000;
const BROWSER_SESSION_START_TIMEOUT_ATTEMPTS = 2;
const LOCAL_PREVIEW_BROWSER_SESSION_RETRY_DELAY_MS = 1_000;

type BrowserApi = NonNullable<EnvironmentApi["browser"]>;

export interface EnsureBrowserSessionOptions {
  readonly allowWhileConnecting?: boolean;
  readonly retryHiddenLocalPreviewFailure?: boolean;
}

interface BrowserOpenSessionOptions {
  readonly browserBusy: boolean;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly projectId: number | null;
  readonly session: BrowserSessionSnapshot | null;
  readonly setBrowserRuntimeError: (message: string | null) => void;
  readonly setConnecting: (connecting: boolean) => void;
  readonly setOpeningBrowserSession: (opening: boolean) => void;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly showBrowserError: (error: unknown) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function reportBrowserSessionUnavailable(input: {
  readonly setBrowserRuntimeError: (message: string | null) => void;
}): void {
  const message = browserSessionUnavailableMessage();
  input.setBrowserRuntimeError(message);
  toastManager.add({
    type: "error",
    title: "Browser session unavailable",
    description: message,
  });
}

async function openInitialBrowserSession(input: {
  readonly browserApi: BrowserApi;
  readonly devTunnelRecords: ReadonlyArray<DevTunnelRecord>;
  readonly environmentId: EnvironmentId;
  readonly initialUrl: string;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}): Promise<boolean> {
  const opened = await withBrowserOperationTimeout(
    "Browser session open",
    input.browserApi.openSession({
      threadId: input.threadId,
      environmentId: input.environmentId,
      initialUrl: input.initialUrl,
    }),
    BROWSER_SESSION_START_TIMEOUT_MS,
  );
  if (!opened) {
    return false;
  }

  let snapshot = opened;
  if (!browserSessionHasVisibleAddressIntent(opened, input.initialUrl, input.devTunnelRecords)) {
    const result = await withBrowserOperationTimeout(
      "Browser tab open",
      input.browserApi.command({
        threadId: input.threadId,
        environmentId: input.environmentId,
        browserId: opened.browserId,
        command: { type: "newTab", url: input.initialUrl, makeActive: true },
      }),
      BROWSER_SESSION_START_TIMEOUT_MS,
    );
    snapshot = result.snapshot;
  }
  input.setSessionSnapshot(input.threadRef, snapshot);
  return browserSessionHasVisibleAddressIntent(snapshot, input.initialUrl, input.devTunnelRecords);
}

async function closeStaleBrowserSessionBeforeRetry(input: {
  readonly browserApi: BrowserApi;
  readonly environmentId: EnvironmentId;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}): Promise<void> {
  try {
    await withBrowserOperationTimeout(
      "Browser session close",
      input.browserApi.closeSession({
        threadId: input.threadId,
        environmentId: input.environmentId,
      }),
      BROWSER_SESSION_CLOSE_TIMEOUT_MS,
    );
  } catch {
    // Best-effort stale-state cleanup before reopening the local preview.
  }
  useBrowserStateStore.getState().clearBrowserState(input.threadRef);
}

async function openBrowserSessionWithRetries(input: {
  readonly browserApi: BrowserApi;
  readonly devTunnelRecordsRef: MutableRefObject<ReadonlyArray<DevTunnelRecord>>;
  readonly environmentId: EnvironmentId;
  readonly initialUrl: string;
  readonly options: EnsureBrowserSessionOptions;
  readonly setSessionSnapshot: (
    threadRef: ScopedThreadRef,
    snapshot: BrowserSessionSnapshot,
  ) => void;
  readonly showBrowserError: (error: unknown) => void;
  readonly threadId: ThreadId;
  readonly threadRef: ScopedThreadRef;
}): Promise<boolean> {
  const attempts = browserSessionOpenAttemptCount({
    url: input.initialUrl,
    devTunnelRecords: input.devTunnelRecordsRef.current,
    minimumAttempts: BROWSER_SESSION_START_TIMEOUT_ATTEMPTS,
  });
  const shouldRetryHiddenFailure = input.options.retryHiddenLocalPreviewFailure !== false;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const opened = await openInitialBrowserSession({
        browserApi: input.browserApi,
        devTunnelRecords: input.devTunnelRecordsRef.current,
        environmentId: input.environmentId,
        initialUrl: input.initialUrl,
        setSessionSnapshot: input.setSessionSnapshot,
        threadId: input.threadId,
        threadRef: input.threadRef,
      });
      if (opened) {
        return true;
      }
      if (
        !shouldRetryHiddenLocalPreviewSessionOpen({
          attempt,
          attempts,
          enabled: shouldRetryHiddenFailure,
          url: input.initialUrl,
          devTunnelRecords: input.devTunnelRecordsRef.current,
        })
      ) {
        return false;
      }
    } catch (error) {
      if (
        !shouldRetryBrowserSessionOpenError({
          attempt,
          attempts,
          url: input.initialUrl,
          error,
          devTunnelRecords: input.devTunnelRecordsRef.current,
        })
      ) {
        input.showBrowserError(error);
        return false;
      }
    }
    await closeStaleBrowserSessionBeforeRetry({
      browserApi: input.browserApi,
      environmentId: input.environmentId,
      threadId: input.threadId,
      threadRef: input.threadRef,
    });
    await sleep(LOCAL_PREVIEW_BROWSER_SESSION_RETRY_DELAY_MS);
  }
  return false;
}

export function useBrowserOpenSession({
  browserBusy,
  devTunnelRecordsRef,
  environmentId,
  projectId,
  session,
  setBrowserRuntimeError,
  setConnecting,
  setOpeningBrowserSession,
  setSessionSnapshot,
  showBrowserError,
  threadId,
  threadRef,
}: BrowserOpenSessionOptions): (
  initialUrl: string,
  options?: EnsureBrowserSessionOptions,
) => Promise<boolean> {
  return useCallback(
    async (initialUrl: string, options: EnsureBrowserSessionOptions = {}): Promise<boolean> => {
      if (session) return true;
      if (!shouldOpenBrowserSession({ hasSession: false, browserBusy, ...options })) {
        return false;
      }
      try {
        setConnecting(true);
        setOpeningBrowserSession(true);
        setBrowserRuntimeError(null);
        const result = await runThreadExecutionIntentAction(
          environmentId,
          {
            action: "browser",
            projectId,
            targetId: threadId,
            dispatchIdentity: newCommandId(),
          },
          {
            dispatch: async (api) => {
              const browserApi = api.browser;
              if (!browserApi) {
                reportBrowserSessionUnavailable({ setBrowserRuntimeError });
                return false;
              }
              return openBrowserSessionWithRetries({
                browserApi,
                devTunnelRecordsRef,
                environmentId,
                initialUrl,
                options,
                setSessionSnapshot,
                showBrowserError,
                threadId,
                threadRef,
              });
            },
            wasAccepted: Boolean,
          },
        );
        return result.kind === "already-dispatched" ? true : result.value;
      } catch (error) {
        showBrowserError(error);
        return false;
      } finally {
        setOpeningBrowserSession(false);
        setConnecting(false);
      }
    },
    [
      browserBusy,
      devTunnelRecordsRef,
      environmentId,
      projectId,
      session,
      setBrowserRuntimeError,
      setConnecting,
      setOpeningBrowserSession,
      setSessionSnapshot,
      showBrowserError,
      threadId,
      threadRef,
    ],
  );
}
